import { useEffect, useState } from "react";
import { useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import {
  overviewStatusQueryOptions,
  MAX_POLL_DURATION_MS,
  type TopicDetailAsyncResult,
} from "@/lib/queries/overview.queries";

interface UseTopicDetailAsyncResourceArgs<T> {
  summary: TopicDetailAsyncResult<T> | null | undefined;
  targetKey: QueryKey;
  resetKey: string;
  enabled: boolean;
}

interface UseTopicDetailAsyncResourceResult<T> {
  data: T[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  timedOut: boolean;
}

/**
 * Polls `/overview/status/:id` for topic-detail Examples/Fatigue jobs — mirrors
 * `useOverviewGroupByOptions` with `resultField: 'data'`.
 */
export function useTopicDetailAsyncResource<T>({
  summary,
  targetKey,
  resetKey,
  enabled,
}: UseTopicDetailAsyncResourceArgs<T>): UseTopicDetailAsyncResourceResult<T> {
  const queryClient = useQueryClient();
  const ready = summary?.status === "ready";

  const [poll, setPoll] = useState<{ correlationId: string } | null>(null);
  const [pollStartedAt, setPollStartedAt] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    setPoll(null);
    setPollStartedAt(0);
    setTimedOut(false);
  }, [resetKey]);

  useEffect(() => {
    if (ready || !enabled || poll) return;
    if (summary?.status === "pending") {
      setPoll({ correlationId: summary.correlationId });
      setPollStartedAt(Date.now());
      setTimedOut(false);
    }
  }, [summary, ready, enabled, poll]);

  useEffect(() => {
    if (!pollStartedAt || ready) return;
    const remaining = MAX_POLL_DURATION_MS - (Date.now() - pollStartedAt);
    if (remaining <= 0) {
      setTimedOut(true);
      return;
    }
    const id = setTimeout(() => setTimedOut(true), remaining);
    return () => clearTimeout(id);
  }, [pollStartedAt, ready]);

  const correlationId = poll?.correlationId ?? null;
  const { data: statusData, error: statusError } = useQuery({
    ...overviewStatusQueryOptions(correlationId, pollStartedAt),
    enabled: !!correlationId && pollStartedAt > 0 && !ready,
  });

  useEffect(() => {
    if (statusData?.status !== "COMPLETED" || !poll) return;
    const rows = (statusData.data ?? []) as T[];
    queryClient.setQueryData(targetKey, { status: "ready", data: rows });
    setPoll(null);
    setPollStartedAt(0);
    setTimedOut(false);
  }, [statusData, poll, queryClient, targetKey]);

  const polledRows =
    statusData?.status === "COMPLETED" && poll
      ? ((statusData.data ?? []) as T[])
      : undefined;

  const data = summary?.status === "ready" ? summary.data : polledRows ?? [];
  const isLoading = enabled && !ready && !timedOut && statusData?.status !== "FAILED" && polledRows === undefined;
  const pollFailed = statusData?.status === "FAILED" || timedOut;
  const isError = enabled && !ready && pollFailed;
  const error = !isError
    ? null
    : timedOut
      ? new Error("Topic detail request timed out")
      : statusData?.status === "FAILED"
        ? new Error(statusData.error ?? statusData.message ?? "Topic detail request failed")
        : statusError;

  return { data, isLoading, isError, error, timedOut };
}
